import React, {useState} from 'react';
import {Box, TextField, styled, InputAdornment} from '@mui/material';
import {
  Body,
  designSystemToken,
  Icon,
  Label,
} from '@lightricks/react-design-system';
import translate from '@/utils/translate';
import Chip from '@/components/chip';
import Divider from '@/components/divider';
import CustomGoalTextField from './CustomGoalPopper';
import InputFieldContainer from './InputFieldContainer';

// Styled Components
const FormSection = styled(Box)(() => ({
  display: 'flex',
  width: '100%',
  flexDirection: 'column',
  flex: 1,
}));

const GoalSelectionContainer = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
  paddingBottom: '24px',
}));

const GoalChipsGrid = styled(Box)(() => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: '8px',
}));

const StyledTextField = styled(TextField)(() => ({
  '& .MuiOutlinedInput-root': {
    'borderRadius': '8px',
    'backgroundColor': designSystemToken('semantic.bg.secondary'),
    'borderColor': designSystemToken('semantic.fg.tertiary'),
    '&:hover': {
      borderColor: designSystemToken('semantic.fg.primary'),
    },
    '&.Mui-focused': {
      borderColor: designSystemToken('semantic.bg.primary'),
    },
  },
  '& .MuiInputBase-input': {
    padding: '12px 16px',
    fontSize: '14px',
    lineHeight: '18px',
  },
}));

const DeleteIconContainer = styled(Box)(() => ({
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const shouldNotForwardProps = ['isSelected'];

const StyledChip = styled(Chip, {
  shouldForwardProp: (prop: string) => !shouldNotForwardProps.includes(prop),
})<{isSelected?: boolean}>(({isSelected}) => ({
  'backgroundColor': isSelected
    ? designSystemToken('semantic.bg.alpha')
    : 'transparent',
  'color': isSelected
    ? designSystemToken('semantic.fg.onPrimary')
    : designSystemToken('semantic.fg.primary'),
  'borderColor': isSelected
    ? designSystemToken('semantic.bg.neutral')
    : designSystemToken('semantic.fg.tertiary'),
  'borderRadius': '20px',
  'height': '32px',
  'minWidth': '56px',
  'fontSize': '14px',
  'fontWeight': 500,
  'borderWidth': isSelected ? '2px' : '1px',
  '& .MuiChip-deleteIcon': {
    color: designSystemToken('semantic.fg.primary'),
    fontSize: '16px',
  },
}));

interface ChipOption {
  id: string;
  localeLabelKey: string;
}

interface ProjectBasicsFormProps {
  testID?: string;
  // Text input section (optional)
  showTextInput?: boolean;
  promoting?: string;
  onPromotingChange?: (value: string) => void;
  promotingLabel?: string;
  promotingPlaceholder?: string;
  maxCharacters?: number;
  showCharacterCounter?: boolean;
  // Chip selection section
  chipOptions: ChipOption[];
  selectedValues: string[];
  onValueSelect: (value: string) => void;
  chipSectionLabel: string;
  // Custom goal functionality (optional)
  showCustomOption?: boolean;
  customId?: string;
  customValue?: string;
  onCustomValueAccept?: (customText: string) => void;
  onCustomValueDelete?: () => void;
  onCustomPopperOpen?: (event?: React.MouseEvent<HTMLElement>) => void;
  isCustomPopperOpen?: boolean;
  onCustomPopperClose?: () => void;
  customPopperAnchorEl?: HTMLElement | null;
}

function ProjectBasicsForm({
  testID = 'project-basics-form',
  // Text input props
  showTextInput = true,
  promoting = '',
  onPromotingChange,
  promotingLabel = '',
  promotingPlaceholder = '',
  maxCharacters,
  showCharacterCounter = false,
  // Chip selection props
  chipOptions,
  selectedValues,
  onValueSelect,
  chipSectionLabel,
  // Custom option props
  showCustomOption = false,
  customId = 'other',
  customValue = '',
  onCustomValueAccept,
  onCustomValueDelete,
  onCustomPopperOpen,
  isCustomPopperOpen = false,
  onCustomPopperClose,
  customPopperAnchorEl,
}: ProjectBasicsFormProps) {
  const [isFocused, setIsFocused] = useState(false);

  const handlePromotingChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    if (maxCharacters && newValue.length > maxCharacters) {
      return;
    }
    onPromotingChange?.(newValue);
  };

  const shouldShowCounter = !!(
    showCharacterCounter &&
    maxCharacters &&
    isFocused
  );

  const remainingCharacters = maxCharacters
    ? maxCharacters - promoting.length
    : 0;

  return (
    <FormSection data-testid={testID}>
      {showTextInput && (
        <>
          <InputFieldContainer>
            <Body size="sm" color={designSystemToken('semantic.fg.primary')}>
              {translate(promotingLabel)}
            </Body>
            <StyledTextField
              placeholder={translate(promotingPlaceholder)}
              value={promoting}
              onChange={handlePromotingChange}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
              inputProps={
                maxCharacters ? {maxLength: maxCharacters} : undefined
              }
              {...(shouldShowCounter
                ? {
                    InputProps: {
                      endAdornment: (
                        <InputAdornment position="end">
                          <Label
                            size="xs"
                            color={designSystemToken('semantic.fg.secondary')}
                          >
                            {remainingCharacters}
                          </Label>
                        </InputAdornment>
                      ),
                    },
                  }
                : {})}
              fullWidth
              variant="outlined"
            />
          </InputFieldContainer>
          <Divider margin={0} style={{marginBottom: '16px'}} />
        </>
      )}

      {/* Chip Selection */}
      <GoalSelectionContainer>
        <Body size="sm" color={designSystemToken('semantic.fg.primary')}>
          {translate(chipSectionLabel)}
        </Body>

        <GoalChipsGrid>
          {chipOptions.map((option) => {
            const isCustom = showCustomOption && option.id === customId;
            const displayLabel =
              isCustom && customValue
                ? customValue
                : translate(option.localeLabelKey);

            const hasCustomValue = isCustom && customValue;
            const isSelected = selectedValues.includes(option.id);

            return (
              <StyledChip
                key={option.id}
                label={displayLabel}
                variant={isSelected ? 'filled' : 'outlined'}
                onClick={(event) => {
                  if (isCustom && !hasCustomValue) {
                    onCustomPopperOpen?.(event);
                  } else {
                    onValueSelect(option.id);
                  }
                }}
                isSelected={isSelected}
                handleDelete={hasCustomValue ? onCustomValueDelete : undefined}
                deleteIcon={
                  hasCustomValue ? (
                    <DeleteIconContainer>
                      <Icon
                        name="Actions-Close-Small"
                        size="small"
                        appearance="neutral"
                        color={designSystemToken('semantic.fg.primary')}
                        style={{width: '24px', height: '24px'}}
                      />
                    </DeleteIconContainer>
                  ) : undefined
                }
              />
            );
          })}
        </GoalChipsGrid>
      </GoalSelectionContainer>

      {/* Custom Value Text Field */}
      {showCustomOption && onCustomPopperClose && onCustomValueAccept && (
        <CustomGoalTextField
          open={isCustomPopperOpen}
          anchorEl={customPopperAnchorEl || null}
          onClose={onCustomPopperClose}
          onSubmit={onCustomValueAccept}
          initialValue={customValue}
        />
      )}
    </FormSection>
  );
}

export default ProjectBasicsForm;
